import { Navigate, Outlet, useLocation } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import { useAuthStore } from "../../stores/authStore";
import toast from "react-hot-toast";
import { useEffect, useRef } from "react";

interface PermissionProtectedRouteProps {
  /**
   * Single permission required to access route
   */
  requiredPermission?: string;
  /**
   * Multiple permissions - user needs ANY of these (OR logic)
   */
  anyPermission?: string[];
  /**
   * Multiple permissions - user needs ALL of these (AND logic)
   */
  allPermissions?: string[];
  /**
   * Role required to access route
   */
  requiredRole?: string;
  /**
   * Redirect path when access denied
   * @default "/dashboard"
   */
  redirectTo?: string;
  /**
   * Children to render
   */
  children?: React.ReactNode;
}

/**
 * Route guard component that checks permissions before rendering
 *
 * @example
 * // In routes/index.tsx
 * <Route
 *   path="/users"
 *   element={
 *     <PermissionProtectedRoute requiredPermission="users:view">
 *       <UsersPage />
 *     </PermissionProtectedRoute>
 *   }
 * />
 *
 * @example
 * // With role check
 * <Route
 *   path="/settings/billing"
 *   element={
 *     <PermissionProtectedRoute requiredRole="BUSINESS_OWNER">
 *       <BillingSettingsPage />
 *     </PermissionProtectedRoute>
 *   }
 * />
 */
export function PermissionProtectedRoute({
  requiredPermission,
  anyPermission,
  allPermissions,
  requiredRole,
  redirectTo = "/dashboard",
  children,
}: PermissionProtectedRouteProps) {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole } =
    usePermissions();
  const hasShownToast = useRef(false);

  // If user is not loaded yet, show loading
  if (!user) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  let hasAccess = true;
  let deniedReason = "";

  // Check single permission
  if (requiredPermission) {
    if (!hasPermission(requiredPermission)) {
      hasAccess = false;
      deniedReason = `Missing permission: ${requiredPermission}`;
    }
  }

  // Check any permission (OR)
  if (anyPermission && anyPermission.length > 0) {
    if (!hasAnyPermission(anyPermission)) {
      hasAccess = false;
      deniedReason = `Missing one of permissions: ${anyPermission.join(", ")}`;
    }
  }

  // Check all permissions (AND)
  if (allPermissions && allPermissions.length > 0) {
    if (!hasAllPermissions(allPermissions)) {
      hasAccess = false;
      deniedReason = `Missing required permissions: ${allPermissions.join(", ")}`;
    }
  }

  // Check role
  if (requiredRole) {
    if (!hasRole(requiredRole)) {
      hasAccess = false;
      deniedReason = `Requires ${requiredRole} role or higher`;
    }
  }

  // Show toast on access denied
  useEffect(() => {
    if (!hasAccess && !hasShownToast.current) {
      toast.error(`Access denied: ${deniedReason}`, {
        duration: 4000,
        id: "permission-access-denied",
      });
      hasShownToast.current = true;
    }
  }, [hasAccess, deniedReason]);

  if (!hasAccess) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
